import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  TablePagination,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Search,
  Person,
  Upload,
  VerifiedUser,
  Warning,
  Business,
  Visibility,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost, apiPut } from '../services/apiClient';
import { LoggerService } from '../services/LoggerService';

// --- Interfaces ---

interface Investor {
  id: string;
  code: string;
  legal_name: string;
  display_name: string;
  investor_type: string;
  status: string;
  kyc_status: string;
  risk_rating: string;
  country: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postal_code: string;
  tax_residency: string;
  is_us_person: boolean;
  fatca_status: string;
  qualified_investor: boolean;
  created_at: string;
  updated_at: string;
}

interface KYCDocument {
  id: string;
  document_type: string;
  filename: string;
  status: string;
  uploaded_at: string;
  expires_at: string | null;
}

interface BeneficialOwner {
  id: string;
  full_name: string;
  nationality: string;
  ownership_percentage: number;
  is_pep: boolean;
  verified: boolean;
}

interface Position {
  id: string;
  share_class_name: string;
  isin: string;
  units: number;
  cost_basis: number;
  market_value: number;
  unrealized_pnl: number;
  currency: string;
}

interface InvestorDetail extends Investor {
  documents: KYCDocument[];
  ubos: BeneficialOwner[];
  positions: Position[];
}

interface InvestorListResponse {
  items: Investor[];
  total: number;
  page: number;
  page_size: number;
}

interface CreateInvestorForm {
  legal_name: string;
  display_name: string;
  investor_type: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postal_code: string;
  country: string;
  tax_residency: string;
  is_us_person: boolean;
  fatca_status: string;
  risk_rating: string;
  qualified_investor: boolean;
}

interface UBOForm {
  full_name: string;
  nationality: string;
  ownership_percentage: number;
  is_pep: boolean;
}

const EMPTY_INVESTOR_FORM: CreateInvestorForm = {
  legal_name: '',
  display_name: '',
  investor_type: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postal_code: '',
  country: '',
  tax_residency: '',
  is_us_person: false,
  fatca_status: '',
  risk_rating: '',
  qualified_investor: false,
};

const EMPTY_UBO_FORM: UBOForm = {
  full_name: '',
  nationality: '',
  ownership_percentage: 0,
  is_pep: false,
};

const INVESTOR_TYPES = ['individual', 'institutional', 'corporate', 'trust', 'fund_of_funds'];
const STATUS_OPTIONS = ['draft', 'pending_review', 'approved', 'active', 'suspended', 'closed'];
const KYC_STATUS_OPTIONS = ['not_started', 'documents_pending', 'under_review', 'approved', 'expired', 'rejected'];
const RISK_RATINGS = ['low', 'medium', 'high', 'very_high'];
const FATCA_STATUSES = ['not_applicable', 'compliant', 'non_compliant', 'pending', 'exempt'];
const DOCUMENT_TYPES = ['passport', 'id_card', 'proof_of_address', 'tax_certificate', 'incorporation_docs', 'beneficial_ownership', 'source_of_wealth', 'other'];

const STATUS_CHIP_MAP: Record<string, 'default' | 'info' | 'primary' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  pending_review: 'info',
  approved: 'primary',
  active: 'success',
  suspended: 'warning',
  closed: 'error',
};

const KYC_CHIP_MAP: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  not_started: 'default',
  documents_pending: 'info',
  under_review: 'warning',
  approved: 'success',
  expired: 'error',
  rejected: 'error',
};

const formatLabel = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatCurrency = (value: number, currency: string): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// --- Component ---

const InvestorsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // List state
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterKYC, setFilterKYC] = useState('');
  const [filterRisk, setFilterRisk] = useState('');

  // Detail state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState(0);
  const [selectedInvestor, setSelectedInvestor] = useState<InvestorDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<CreateInvestorForm>(EMPTY_INVESTOR_FORM);

  // Create state
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateInvestorForm>(EMPTY_INVESTOR_FORM);
  const [createLoading, setCreateLoading] = useState(false);

  // KYC upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);

  // UBO state
  const [uboDialogOpen, setUboDialogOpen] = useState(false);
  const [uboForm, setUboForm] = useState<UBOForm>(EMPTY_UBO_FORM);
  const [uboLoading, setUboLoading] = useState(false);

  // --- Data Loading ---

  const loadInvestors = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      if (searchQuery) params.search = searchQuery;
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.investor_type = filterType;
      if (filterKYC) params.kyc_status = filterKYC;
      if (filterRisk) params.risk_rating = filterRisk;

      const response = await apiGet<InvestorListResponse>('/investors', { params });
      setInvestors(response.items);
      setTotalCount(response.total);
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to load investors';
      LoggerService.error('Failed to load investors', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const loadInvestorDetail = async (investorId: string) => {
    setDetailLoading(true);
    try {
      const raw = await apiGet<any>(`/investors/${investorId}`);
      const detail: InvestorDetail = {
        ...raw,
        ubos: raw.ubos || raw.beneficial_owners || [],
        documents: raw.documents || [],
        positions: raw.positions || [],
      };
      setSelectedInvestor(detail);
      setEditForm({
        legal_name: detail.legal_name,
        display_name: detail.display_name,
        investor_type: detail.investor_type,
        email: detail.email,
        phone: detail.phone,
        address_line1: detail.address_line1,
        address_line2: detail.address_line2,
        city: detail.city,
        postal_code: detail.postal_code,
        country: detail.country,
        tax_residency: detail.tax_residency,
        is_us_person: detail.is_us_person,
        fatca_status: detail.fatca_status,
        risk_rating: detail.risk_rating,
        qualified_investor: detail.qualified_investor,
      });
    } catch (err: any) {
      LoggerService.error('Failed to load investor detail', err);
      enqueueSnackbar('Failed to load investor details', { variant: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadInvestors();
  }, [page, rowsPerPage, filterStatus, filterType, filterKYC, filterRisk]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(0);
      loadInvestors();
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // --- Actions ---

  const handleViewDetail = (investor: Investor) => {
    setDetailOpen(true);
    setDetailTab(0);
    setEditMode(false);
    loadInvestorDetail(investor.id);
  };

  const handleCreateInvestor = async () => {
    if (!createForm.legal_name || !createForm.investor_type) {
      enqueueSnackbar('Legal name and investor type are required', { variant: 'warning' });
      return;
    }
    setCreateLoading(true);
    try {
      await apiPost('/investors', createForm);
      enqueueSnackbar('Investor created successfully', { variant: 'success' });
      setCreateOpen(false);
      setCreateForm(EMPTY_INVESTOR_FORM);
      loadInvestors();
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to create investor';
      LoggerService.error('Failed to create investor', err);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateInvestor = async () => {
    if (!selectedInvestor) return;
    try {
      await apiPut(`/investors/${selectedInvestor.id}`, editForm);
      enqueueSnackbar('Investor updated successfully', { variant: 'success' });
      setEditMode(false);
      loadInvestorDetail(selectedInvestor.id);
      loadInvestors();
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to update investor';
      LoggerService.error('Failed to update investor', err);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleKYCUpload = async () => {
    if (!selectedFile || !documentType || !selectedInvestor) return;
    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', documentType);
    try {
      await apiPost(`/investors/${selectedInvestor.id}/kyc`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
      setSelectedFile(null);
      setDocumentType('');
      loadInvestorDetail(selectedInvestor.id);
    } catch (err: any) {
      LoggerService.error('KYC upload failed', err);
      enqueueSnackbar('Failed to upload document', { variant: 'error' });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleAddUBO = async () => {
    if (!selectedInvestor || !uboForm.full_name || uboForm.ownership_percentage <= 0) {
      enqueueSnackbar('Name and ownership percentage are required', { variant: 'warning' });
      return;
    }
    setUboLoading(true);
    try {
      await apiPost(`/investors/${selectedInvestor.id}/beneficial-owners`, uboForm);
      enqueueSnackbar('Beneficial owner added successfully', { variant: 'success' });
      setUboDialogOpen(false);
      setUboForm(EMPTY_UBO_FORM);
      loadInvestorDetail(selectedInvestor.id);
    } catch (err: any) {
      LoggerService.error('Failed to add UBO', err);
      enqueueSnackbar('Failed to add beneficial owner', { variant: 'error' });
    } finally {
      setUboLoading(false);
    }
  };

  // --- Render Helpers ---

  const renderFilters = () => (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
      <TextField
        size="small"
        placeholder="Search investors..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 260 }}
      />
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Status</InputLabel>
        <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{formatLabel(s)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Type</InputLabel>
        <Select value={filterType} label="Type" onChange={(e) => { setFilterType(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {INVESTOR_TYPES.map((t) => (
            <MenuItem key={t} value={t}>{formatLabel(t)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>KYC Status</InputLabel>
        <Select value={filterKYC} label="KYC Status" onChange={(e) => { setFilterKYC(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {KYC_STATUS_OPTIONS.map((k) => (
            <MenuItem key={k} value={k}>{formatLabel(k)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Risk Rating</InputLabel>
        <Select value={filterRisk} label="Risk Rating" onChange={(e) => { setFilterRisk(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {RISK_RATINGS.map((r) => (
            <MenuItem key={r} value={r}>{formatLabel(r)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ flexGrow: 1 }} />
      <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
        New Investor
      </Button>
    </Box>
  );

  const renderInvestorTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }} action={
          <Button color="inherit" size="small" onClick={loadInvestors}>Retry</Button>
        }>
          {error}
        </Alert>
      );
    }

    if (investors.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Person sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No investors found</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
            {searchQuery || filterStatus || filterType || filterKYC || filterRisk
              ? 'Try adjusting your search or filters'
              : 'Create your first investor to get started'}
          </Typography>
          {!searchQuery && !filterStatus && !filterType && !filterKYC && !filterRisk && (
            <Button variant="outlined" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
              Create Investor
            </Button>
          )}
        </Box>
      );
    }

    return (
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Legal Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>KYC Status</TableCell>
                <TableCell>Risk Rating</TableCell>
                <TableCell>Country</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {investors.map((investor) => (
                <TableRow key={investor.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewDetail(investor)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{investor.code}</Typography>
                  </TableCell>
                  <TableCell>{investor.legal_name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={investor.investor_type === 'individual' ? <Person /> : <Business />}
                      label={formatLabel(investor.investor_type)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={formatLabel(investor.status)} color={STATUS_CHIP_MAP[investor.status] || 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={formatLabel(investor.kyc_status)} color={KYC_CHIP_MAP[investor.kyc_status] || 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatLabel(investor.risk_rating)}
                      color={investor.risk_rating === 'high' || investor.risk_rating === 'very_high' ? 'error' : investor.risk_rating === 'medium' ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{investor.country}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewDetail(investor); }}>
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    );
  };

  // --- Detail Dialog Tabs ---

  const renderProfileTab = () => {
    if (!selectedInvestor) return null;

    if (editMode) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Legal Name" value={editForm.legal_name} onChange={(e) => setEditForm({ ...editForm, legal_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Display Name" value={editForm.display_name} onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Investor Type</InputLabel>
                <Select value={editForm.investor_type} label="Investor Type" onChange={(e) => setEditForm({ ...editForm, investor_type: e.target.value })}>
                  {INVESTOR_TYPES.map((t) => <MenuItem key={t} value={t}>{formatLabel(t)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Address Line 1" value={editForm.address_line1} onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Address Line 2" value={editForm.address_line2} onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="City" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Postal Code" value={editForm.postal_code} onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Country" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Tax Residency" value={editForm.tax_residency} onChange={(e) => setEditForm({ ...editForm, tax_residency: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>FATCA Status</InputLabel>
                <Select value={editForm.fatca_status} label="FATCA Status" onChange={(e) => setEditForm({ ...editForm, fatca_status: e.target.value })}>
                  {FATCA_STATUSES.map((f) => <MenuItem key={f} value={f}>{formatLabel(f)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Risk Rating</InputLabel>
                <Select value={editForm.risk_rating} label="Risk Rating" onChange={(e) => setEditForm({ ...editForm, risk_rating: e.target.value })}>
                  {RISK_RATINGS.map((r) => <MenuItem key={r} value={r}>{formatLabel(r)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox checked={editForm.is_us_person} onChange={(e) => setEditForm({ ...editForm, is_us_person: e.target.checked })} />}
                label="US Person"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox checked={editForm.qualified_investor} onChange={(e) => setEditForm({ ...editForm, qualified_investor: e.target.checked })} />}
                label="Qualified Investor"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditMode(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateInvestor}>Save Changes</Button>
          </Box>
        </Box>
      );
    }

    const inv = selectedInvestor;
    const fields = [
      { label: 'Code', value: inv.code },
      { label: 'Legal Name', value: inv.legal_name },
      { label: 'Display Name', value: inv.display_name },
      { label: 'Investor Type', value: formatLabel(inv.investor_type) },
      { label: 'Email', value: inv.email },
      { label: 'Phone', value: inv.phone },
      { label: 'Address', value: [inv.address_line1, inv.address_line2, inv.city, inv.postal_code, inv.country].filter(Boolean).join(', ') },
      { label: 'Tax Residency', value: inv.tax_residency },
      { label: 'US Person', value: inv.is_us_person ? 'Yes' : 'No' },
      { label: 'FATCA Status', value: inv.fatca_status ? formatLabel(inv.fatca_status) : 'N/A' },
      { label: 'Risk Rating', value: inv.risk_rating ? formatLabel(inv.risk_rating) : 'N/A' },
      { label: 'Qualified Investor', value: inv.qualified_investor ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(inv.created_at) },
      { label: 'Updated', value: formatDate(inv.updated_at) },
    ];

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" size="small" onClick={() => setEditMode(true)}>Edit</Button>
        </Box>
        <Grid container spacing={1}>
          {fields.map((f) => (
            <Grid item xs={12} sm={6} key={f.label}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              <Typography variant="body2">{f.value || '-'}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderDocumentsTab = () => {
    if (!selectedInvestor) return null;
    const docs = selectedInvestor.documents || [];

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Document Type</InputLabel>
            <Select value={documentType} label="Document Type" onChange={(e) => setDocumentType(e.target.value)}>
              {DOCUMENT_TYPES.map((dt) => (
                <MenuItem key={dt} value={dt}>{formatLabel(dt)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" startIcon={<Upload />} size="small">
            {selectedFile ? selectedFile.name : 'Choose File'}
            <input type="file" hidden onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleKYCUpload}
            disabled={!selectedFile || !documentType || uploadLoading}
            startIcon={uploadLoading ? <CircularProgress size={16} /> : <Upload />}
          >
            Upload
          </Button>
        </Box>
        {docs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No KYC documents uploaded yet
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Filename</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Expires</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {docs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>{formatLabel(doc.document_type)}</TableCell>
                    <TableCell>{doc.filename}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={formatLabel(doc.status)}
                        color={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(doc.uploaded_at)}</TableCell>
                    <TableCell>{doc.expires_at ? formatDate(doc.expires_at) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderUBOsTab = () => {
    if (!selectedInvestor) return null;
    const ubos = selectedInvestor.ubos || [];

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => setUboDialogOpen(true)}>
            Add Beneficial Owner
          </Button>
        </Box>
        {ubos.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No beneficial owners recorded
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Nationality</TableCell>
                  <TableCell align="right">Ownership %</TableCell>
                  <TableCell>PEP</TableCell>
                  <TableCell>Verified</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ubos.map((ubo) => (
                  <TableRow key={ubo.id}>
                    <TableCell>{ubo.full_name}</TableCell>
                    <TableCell>{ubo.nationality}</TableCell>
                    <TableCell align="right">{ubo.ownership_percentage.toFixed(2)}%</TableCell>
                    <TableCell>
                      {ubo.is_pep ? (
                        <Chip size="small" icon={<Warning />} label="PEP" color="warning" />
                      ) : (
                        <Chip size="small" label="No" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {ubo.verified ? (
                        <Chip size="small" icon={<VerifiedUser />} label="Verified" color="success" />
                      ) : (
                        <Chip size="small" label="Pending" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderPositionsTab = () => {
    if (!selectedInvestor) return null;
    const positions = selectedInvestor.positions || [];

    if (positions.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center', mt: 2 }}>
          No positions found for this investor
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Share Class</TableCell>
              <TableCell>ISIN</TableCell>
              <TableCell align="right">Units</TableCell>
              <TableCell align="right">Cost Basis</TableCell>
              <TableCell align="right">Market Value</TableCell>
              <TableCell align="right">Unrealized P&L</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((pos) => (
              <TableRow key={pos.id}>
                <TableCell>{pos.share_class_name}</TableCell>
                <TableCell>{pos.isin}</TableCell>
                <TableCell align="right">{pos.units.toLocaleString('en-US', { maximumFractionDigits: 4 })}</TableCell>
                <TableCell align="right">{formatCurrency(pos.cost_basis, pos.currency)}</TableCell>
                <TableCell align="right">{formatCurrency(pos.market_value, pos.currency)}</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={pos.unrealized_pnl >= 0 ? 'success.main' : 'error.main'}
                    fontWeight={600}
                  >
                    {formatCurrency(pos.unrealized_pnl, pos.currency)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // --- Dialogs ---

  const renderDetailDialog = () => (
    <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setSelectedInvestor(null); setEditMode(false); }} maxWidth="md" fullWidth>
      <DialogTitle>
        {detailLoading ? 'Loading...' : selectedInvestor ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person />
            <span>{selectedInvestor.legal_name}</span>
            <Chip size="small" label={formatLabel(selectedInvestor.status)} color={STATUS_CHIP_MAP[selectedInvestor.status] || 'default'} sx={{ ml: 1 }} />
            <Chip size="small" label={formatLabel(selectedInvestor.kyc_status)} color={KYC_CHIP_MAP[selectedInvestor.kyc_status] || 'default'} />
          </Box>
        ) : 'Investor Detail'}
      </DialogTitle>
      <DialogContent dividers>
        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : selectedInvestor ? (
          <>
            <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Profile" />
              <Tab label="Documents" />
              <Tab label="UBOs" />
              <Tab label="Positions" />
            </Tabs>
            {detailTab === 0 && renderProfileTab()}
            {detailTab === 1 && renderDocumentsTab()}
            {detailTab === 2 && renderUBOsTab()}
            {detailTab === 3 && renderPositionsTab()}
          </>
        ) : (
          <Alert severity="error">Failed to load investor details</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setDetailOpen(false); setSelectedInvestor(null); setEditMode(false); }}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const renderCreateDialog = () => (
    <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setCreateForm(EMPTY_INVESTOR_FORM); }} maxWidth="md" fullWidth>
      <DialogTitle>Create New Investor</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Basic Information</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Legal Name" value={createForm.legal_name} onChange={(e) => setCreateForm({ ...createForm, legal_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Investor Type</InputLabel>
              <Select value={createForm.investor_type} label="Investor Type" onChange={(e) => setCreateForm({ ...createForm, investor_type: e.target.value })}>
                {INVESTOR_TYPES.map((t) => <MenuItem key={t} value={t}>{formatLabel(t)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Display Name" value={createForm.display_name} onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Phone" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Address</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Address Line 1" value={createForm.address_line1} onChange={(e) => setCreateForm({ ...createForm, address_line1: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Address Line 2" value={createForm.address_line2} onChange={(e) => setCreateForm({ ...createForm, address_line2: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City" value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Postal Code" value={createForm.postal_code} onChange={(e) => setCreateForm({ ...createForm, postal_code: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Country" value={createForm.country} onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Tax & Regulatory</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Tax Residency" value={createForm.tax_residency} onChange={(e) => setCreateForm({ ...createForm, tax_residency: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Checkbox checked={createForm.is_us_person} onChange={(e) => setCreateForm({ ...createForm, is_us_person: e.target.checked })} />}
              label="US Person"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>FATCA Status</InputLabel>
              <Select value={createForm.fatca_status} label="FATCA Status" onChange={(e) => setCreateForm({ ...createForm, fatca_status: e.target.value })}>
                {FATCA_STATUSES.map((f) => <MenuItem key={f} value={f}>{formatLabel(f)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Classification</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Risk Rating</InputLabel>
              <Select value={createForm.risk_rating} label="Risk Rating" onChange={(e) => setCreateForm({ ...createForm, risk_rating: e.target.value })}>
                {RISK_RATINGS.map((r) => <MenuItem key={r} value={r}>{formatLabel(r)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox checked={createForm.qualified_investor} onChange={(e) => setCreateForm({ ...createForm, qualified_investor: e.target.checked })} />}
              label="Qualified Investor"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setCreateOpen(false); setCreateForm(EMPTY_INVESTOR_FORM); }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreateInvestor}
          disabled={createLoading || !createForm.legal_name || !createForm.investor_type}
          startIcon={createLoading ? <CircularProgress size={16} /> : <Add />}
        >
          Create Investor
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderUBODialog = () => (
    <Dialog open={uboDialogOpen} onClose={() => { setUboDialogOpen(false); setUboForm(EMPTY_UBO_FORM); }} maxWidth="sm" fullWidth>
      <DialogTitle>Add Beneficial Owner</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField fullWidth required label="Full Name" value={uboForm.full_name} onChange={(e) => setUboForm({ ...uboForm, full_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nationality" value={uboForm.nationality} onChange={(e) => setUboForm({ ...uboForm, nationality: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Ownership %"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={uboForm.ownership_percentage}
              onChange={(e) => setUboForm({ ...uboForm, ownership_percentage: parseFloat(e.target.value) || 0 })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={uboForm.is_pep} onChange={(e) => setUboForm({ ...uboForm, is_pep: e.target.checked })} />}
              label="Politically Exposed Person (PEP)"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setUboDialogOpen(false); setUboForm(EMPTY_UBO_FORM); }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAddUBO}
          disabled={uboLoading || !uboForm.full_name || uboForm.ownership_percentage <= 0}
          startIcon={uboLoading ? <CircularProgress size={16} /> : <Add />}
        >
          Add Owner
        </Button>
      </DialogActions>
    </Dialog>
  );

  // --- Main Render ---

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Person sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight={700}>Investors</Typography>
        <Chip label={`${totalCount} total`} size="small" sx={{ ml: 1 }} />
      </Box>

      <Card>
        <CardContent>
          {renderFilters()}
          {renderInvestorTable()}
        </CardContent>
      </Card>

      {renderDetailDialog()}
      {renderCreateDialog()}
      {renderUBODialog()}
    </Box>
  );
};

export default InvestorsPage;
